/**
 * Auth-related constants and error messages
 */

export const API_BASE_URL =
  import.meta.env.VITE_APP_BASE_URL || 'http://localhost:8000';

// API Endpoints
export const AUTH_ENDPOINTS = {
  PROFILE_DATA: '/users/api/profile/data/',
  PROFILE_UPDATE: '/users/api/profile/',
  AVATAR_UPLOAD: '/users/api/profile/upload-image/',
  API_KEYS_CREATE: '/users/api/api-keys/create/',
  API_KEYS_REVOKE: '/users/api/api-keys/revoke/',
  TOTP_SETUP: '/users/api/totp-setup/',
  TOTP_ACTIVATE: '/users/api/totp-activate/',
  TOTP_DISABLE: '/users/api/totp-disable/',
  DELETE_ACCOUNT: '/users/api/delete-account/',
  DELETE_ACCOUNT_AND_TEAM: '/users/api/delete-account-and-team/',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  PROFILE_SAVED: 'Profile saved successfully.',
  AVATAR_UPLOADED: 'Avatar uploaded successfully.',
  EMAIL_FOOTER_SAVED: 'Preference saved successfully.',
  API_KEY_CREATED: 'API key created successfully.',
  API_KEY_REVOKED: 'API key revoked successfully.',
  TOTP_ENABLED: 'Two-factor authentication has been enabled successfully.',
  TOTP_DISABLED: 'Two-factor authentication has been disabled.',
  TOTP_RESET: '2FA state has been reset. You can now try enabling 2FA again.',
} as const;

// Confirmation Messages
export const CONFIRMATION_MESSAGES = {
  API_KEY_REVOKE:
    'Are you sure you want to revoke this API key? This action cannot be undone.',
  TOTP_DISABLE:
    'Are you sure you want to disable two-factor authentication? This will make your account less secure.',
  TOTP_RESET:
    'Reset 2FA state? This will clear any inconsistent configuration.',
  DELETE_ORG:
    'Are you sure you want to delete this organization? This action cannot be undone.',
  DELETE_USER:
    'Are you sure you want to delete your account? This action cannot be undone.',
  DELETE_USER_AND_ORG:
    'Are you sure you want to delete your account and this organization? This action cannot be undone.',
} as const;

// Email validation
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function validateEmail(value: unknown): string | undefined {
  const s = typeof value === 'string' ? value.trim() : '';
  if (s && !EMAIL_REGEX.test(s)) {
    return 'Please enter a valid email address';
  }
  return undefined;
}

// Invite-specific guard: after the shared validity check passes, reject local parts
// containing characters that are RFC-valid but virtually always typos (backtick, quotes,
// braces, pipes). Apostrophe/plus/percent/dot/underscore/hyphen are allowed (o'brien@,
// first.last+tag@, a-b_c%d@). Does NOT modify the shared validateEmail used elsewhere.
const INVITE_EMAIL_LOCAL_PART_REGEX = /^[A-Za-z0-9._%+'-]+$/;
export function validateInviteEmail(value: unknown): string | undefined {
  const base = validateEmail(value);
  if (base !== undefined) {
    return base;
  }
  const s = typeof value === 'string' ? value.trim() : '';
  if (!s) {
    return undefined;
  }
  const localPart = s.slice(0, s.lastIndexOf('@'));
  if (!INVITE_EMAIL_LOCAL_PART_REGEX.test(localPart)) {
    return 'Please enter a valid email address';
  }
  return undefined;
}

// Valid profile tabs
export const PROFILE_TABS = ['account', 'integrations', 'advanced'] as const;

export type ProfileTab = (typeof PROFILE_TABS)[number];

export const TOTP_FIELDS = {
  code: {
    required: true,
    validate: (value: unknown) =>
      typeof value === 'string' && /^\d{6}$/.test(value)
        ? undefined
        : 'Code must be exactly 6 digits',
  },
} as const;
